import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, Subscription } from 'rxjs';
import { BreadcrumbsService } from '../../../../shared/breadcrumbs/breadcrumbs.service';
import { BottomNavService, BottomNavUpdateButtonsList } from '../../../../shared/bottom-nav/bottom-nav.service';
import { ZiroIcons } from '../../../../shared/models/ziro-icons.enum';
import { ButtonStyles } from '../../../../button/button.component';
import { TranslateService } from '@ngx-translate/core';
import {
  RingCentralAutoAttendantReportEntry,
  RingCentralAutoAttendantReportEntryKeyPress,
  RingCentralAutoAttendantReportEntryKeyPressAction,
  RingCentralAutoAttendantReportEntryKeyPressStarPound,
  RingCentralAutoAttendantReportEntryNoInputAction,
  RingCentralExportDetails,
} from '../../../../shared/models/generated/ziroModels';
import { MigrationsAssessmentsContext } from '../../../services/migrations-assessments.context';
import { RingCentralAutoAttendantsContext } from '../../../services/ring-central/ring-central-auto-attendants.context';
import { AudioFileResource } from '../../../../shared/resources/audio-file.resource';
import { TelephoneNumberFilter } from '../../../../shared/filters/telephone-number.filter';

interface ReadOnlyMenuButton {
  keyLabel: string;
  action: string;
  isConfigured: boolean;
  isNoInput: boolean;
}

type MenuButton =
  | 'button0'
  | 'button1'
  | 'button2'
  | 'button3'
  | 'button4'
  | 'button5'
  | 'button6'
  | 'button7'
  | 'button8'
  | 'button9';

const MENU_BUTTONS: Array<{ menuButton: MenuButton; label: string }> = [
  { menuButton: 'button0', label: '0' },
  { menuButton: 'button1', label: '1' },
  { menuButton: 'button2', label: '2' },
  { menuButton: 'button3', label: '3' },
  { menuButton: 'button4', label: '4' },
  { menuButton: 'button5', label: '5' },
  { menuButton: 'button6', label: '6' },
  { menuButton: 'button7', label: '7' },
  { menuButton: 'button8', label: '8' },
  { menuButton: 'button9', label: '9' },
];

@Component({
  selector: 'ziro-ring-central-auto-attendants-detailed-view',
  templateUrl: './ring-central-auto-attendants-detailed-view.component.html',
  styleUrls: ['./ring-central-auto-attendants-detailed-view.component.scss'],
  standalone: false,
})
export class RingCentralAutoAttendantsDetailedViewComponent implements OnInit, OnDestroy {
  private _bottomNavService = inject(BottomNavService);
  private _breadcrumbsService = inject(BreadcrumbsService);
  private _router = inject(Router);
  private _route = inject(ActivatedRoute);
  private _migrationsAssessmentsContext = inject(MigrationsAssessmentsContext);
  private _ringCentralAutoAttendantsContext = inject(RingCentralAutoAttendantsContext);
  private _audioFileResource = inject(AudioFileResource);
  private _translateService = inject(TranslateService);
  private _telephoneNumberFilter = inject(TelephoneNumberFilter);

  private _subs = new Subscription();
  private _tenantId: string;
  private _exportId: string;
  private _exportName: string;
  private _autoAttendantId: string;
  private _allEntries: RingCentralAutoAttendantReportEntry[] = [];

  ziroIcons = ZiroIcons;
  isLoading = true;
  isSideNavCollapsed = false;
  allAutoAttendants: RingCentralAutoAttendantReportEntry[] = [];
  autoAttendant: RingCentralAutoAttendantReportEntry;
  directNumbers: string;
  greetingAudioUrl: string;
  showUnconfiguredButtons = false;
  configuredButtons: ReadOnlyMenuButton[] = [];
  unconfiguredButtons: ReadOnlyMenuButton[] = [];
  noInputButton: ReadOnlyMenuButton;

  ngOnInit(): void {
    ({
      tenantId: this._tenantId,
      id: this._exportId,
      autoAttendantId: this._autoAttendantId,
    } = this._route.snapshot.params);
    this._initBottomNav();

    const contextSub = combineLatest([
      this._ringCentralAutoAttendantsContext.state$,
      this._migrationsAssessmentsContext.state$,
    ]).subscribe(([entries, assessments]) => {
      this._allEntries = entries;
      this.allAutoAttendants = [...entries].sort((a, b) => a.name.localeCompare(b.name));
      this._exportName = assessments.ringCentralExports.find(
        (ringCentralExport: RingCentralExportDetails) => ringCentralExport.exportId === this._exportId,
      )?.name;
      const autoAttendant = this._allEntries.find((entry) => entry.id === this._autoAttendantId);
      if (!autoAttendant) {
        return;
      }

      this.autoAttendant = autoAttendant;
      this.directNumbers = autoAttendant.autoAttendantDirectNumbers
        .map((directNumber) => this._telephoneNumberFilter.transform(directNumber.number))
        .join(', ');
      if (autoAttendant.greeting?.audioFile) {
        this._loadGreetingAudio(autoAttendant.greeting.audioFile.filename);
      } else {
        this.greetingAudioUrl = null;
      }
      this._buildMenuButtons();
      this._initBreadcrumbs(this._exportName);
      this.isLoading = false;
    });
    this._subs.add(contextSub);
  }

  ngOnDestroy(): void {
    this._subs.unsubscribe();
  }

  toggleSidebar(): void {
    this.isSideNavCollapsed = !this.isSideNavCollapsed;
  }

  navigateToAutoAttendant(id: string): void {
    if (!id || id === this._autoAttendantId) {
      return;
    }
    this._router.navigate(['..', id], { relativeTo: this._route });
  }

  toggleUnconfiguredButtons(): void {
    this.showUnconfiguredButtons = !this.showUnconfiguredButtons;
  }

  private _loadGreetingAudio(filename: string): void {
    const audioSub = this._audioFileResource
      .getAudioFile(this._tenantId, 'ring-central', filename, this._exportId)
      .subscribe((file) => {
        this.greetingAudioUrl = URL.createObjectURL(file);
      });
    this._subs.add(audioSub);
  }

  private _buildMenuButtons(): void {
    const configured: ReadOnlyMenuButton[] = [];
    const unconfigured: ReadOnlyMenuButton[] = [];

    for (const button of MENU_BUTTONS) {
      const keyPress = this.autoAttendant.keyPresses[button.menuButton];
      if (!keyPress) {
        continue;
      }
      const isConfigured = keyPress.action !== RingCentralAutoAttendantReportEntryKeyPressAction.NONE;
      const btn: ReadOnlyMenuButton = {
        keyLabel: button.label,
        action: isConfigured ? this._getKeyPressActionLabel(keyPress) : '',
        isConfigured,
        isNoInput: false,
      };
      if (isConfigured) {
        configured.push(btn);
      } else {
        unconfigured.push(btn);
      }
    }

    if (this.autoAttendant.keyPresses.buttonStar) {
      configured.push({
        keyLabel: '*',
        action: this._translateService.instant(
          this._getSpecialButtonActionTkey(this.autoAttendant.keyPresses.buttonStar),
        ),
        isConfigured: true,
        isNoInput: false,
      });
    }

    if (this.autoAttendant.keyPresses.buttonPound) {
      configured.push({
        keyLabel: '#',
        action: this._translateService.instant(
          this._getSpecialButtonActionTkey(this.autoAttendant.keyPresses.buttonPound),
        ),
        isConfigured: true,
        isNoInput: false,
      });
    }

    const noInputs = this.autoAttendant.keyPresses.noInputs;
    if (noInputs) {
      this.noInputButton = {
        keyLabel: this._translateService.instant(
          'tkey;migrations.ring_central.auto_attendants_report.explorer.menu_buttons.no_inputs',
        ),
        action: this._getNoInputActionLabel(noInputs.action, noInputs.destination),
        isConfigured: true,
        isNoInput: true,
      };
    }

    this.configuredButtons = configured;
    this.unconfiguredButtons = unconfigured;
  }

  private _getKeyPressActionLabel(keyPress: RingCentralAutoAttendantReportEntryKeyPress): string {
    switch (keyPress.action) {
      case RingCentralAutoAttendantReportEntryKeyPressAction.CONNECT_TO:
        return this._translateService.instant(
          'tkey;migrations.ring_central.auto_attendants_report.explorer.menu_buttons.action.connect_to',
          { destination: keyPress.destination },
        );
      case RingCentralAutoAttendantReportEntryKeyPressAction.TRANSFER_TO_VOICEMAIL:
        return this._translateService.instant(
          'tkey;migrations.ring_central.auto_attendants_report.explorer.menu_buttons.action.transfer_to_voicemail',
          { destination: keyPress.destination },
        );
      case RingCentralAutoAttendantReportEntryKeyPressAction.CONNECT_TO_DIAL_BY_NAME_DIRECTORY:
        return this._translateService.instant(
          'tkey;migrations.ring_central.auto_attendants_report.explorer.menu_buttons.action.connect_to_dial_by_name_directory',
        );
      case RingCentralAutoAttendantReportEntryKeyPressAction.EXTERNAL_TRANSFER:
        return this._translateService.instant(
          'tkey;migrations.ring_central.auto_attendants_report.explorer.menu_buttons.action.external_transfer',
          { destination: this._telephoneNumberFilter.transform(keyPress.destination) },
        );
      default:
        return this._translateService.instant(
          'tkey;migrations.ring_central.auto_attendants_report.explorer.menu_buttons.action.none',
        );
    }
  }

  private _getSpecialButtonActionTkey(action: RingCentralAutoAttendantReportEntryKeyPressStarPound): string {
    switch (action) {
      case RingCentralAutoAttendantReportEntryKeyPressStarPound.RETURN_TO_PREVIOUS_MENU:
        return 'tkey;migrations.ring_central.auto_attendants_report.explorer.menu_buttons.action.return_to_previous_menu';
      case RingCentralAutoAttendantReportEntryKeyPressStarPound.RETURN_TO_ROOT_MENU:
        return 'tkey;migrations.ring_central.auto_attendants_report.explorer.menu_buttons.action.return_to_root_menu';
      case RingCentralAutoAttendantReportEntryKeyPressStarPound.REPEAT_MENU_GREETING:
        return 'tkey;migrations.ring_central.auto_attendants_report.explorer.menu_buttons.action.repeat_menu_greeting';
      default:
        return String(action);
    }
  }

  private _getNoInputActionLabel(
    action: RingCentralAutoAttendantReportEntryNoInputAction,
    destination: string,
  ): string {
    switch (action) {
      case RingCentralAutoAttendantReportEntryNoInputAction.CONNECT_TO:
        return this._translateService.instant(
          'tkey;migrations.ring_central.auto_attendants_report.explorer.menu_buttons.no_input_action.connect_to',
          { destination },
        );
      case RingCentralAutoAttendantReportEntryNoInputAction.DISCONNECT:
        return this._translateService.instant(
          'tkey;migrations.ring_central.auto_attendants_report.explorer.menu_buttons.no_input_action.disconnect',
        );
      default:
        return String(action);
    }
  }

  private _initBreadcrumbs(exportName: string): void {
    this._breadcrumbsService.updateBreadcrumbs([
      { label: 'tkey;migrations.title', routerLink: true, url: `/migrations/tenants/${this._tenantId}` },
      {
        label: this._translateService.instant('tkey;migrations.number_report.dashboard.breadcrumb', {
          name: exportName,
        }),
        routerLink: true,
        url: `/migrations/tenants/${this._tenantId}/ring-central/${this._exportId}`,
      },
      {
        label: 'tkey;migrations.ring_central.auto_attendants_report.breadcrumb',
        routerLink: true,
        url: `/migrations/tenants/${this._tenantId}/ring-central/${this._exportId}/ring-central-auto-attendants-report`,
      },
      { label: this.autoAttendant?.name },
    ]);
  }

  private _initBottomNav(): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonsList([
        {
          id: 'ring-central-auto-attendants-detailed-view-back',
          dataAutomation: 'ring-central-auto-attendants-detailed-view-back-button',
          label: 'tkey;global.button.back.text',
          buttonClass: ButtonStyles.DEFAULT,
          icon: ZiroIcons.BACK_CIRCLE,
          cb: () => this._router.navigate(['..'], { relativeTo: this._route }),
        },
      ]),
    );
  }
}
